/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { useRouterState } from '@tanstack/react-router'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'
import { forwardRef, useEffect, useMemo, useState } from 'react'

import { NavBadge } from '@/components/blocks/nav-badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import { ACTIVE_BAR_CLASS, LABEL_COLLAPSED_TRANSITION, LABEL_EXPANDED_TRANSITION } from './side-nav-constants'
import { SideNavContext, useSideNavContext } from './side-nav-context'

export interface SideNavExpandableSectionProps {
  icon: ReactElement
  label: string
  badge?: number | boolean
  isActive?: boolean | ((location: { pathname: string }) => boolean)
  defaultExpanded?: boolean
  inactiveBgClassName?: string
  activeBgClassName?: string
  hoverBgClassName?: string
  colorClassName?: string
  hoverColorClassName?: string
  children: ReactNode
}

export const SideNavExpandableSection = forwardRef<HTMLButtonElement, SideNavExpandableSectionProps>(
  function SideNavExpandableSection(props, ref) {
    const {
      icon,
      label,
      badge,
      isActive: isActiveProp,
      defaultExpanded: defaultExpandedProp = false,
      inactiveBgClassName = 'bg-transparent',
      activeBgClassName = 'bg-muted',
      hoverBgClassName = 'hover:bg-card',
      colorClassName = 'text-muted-foreground',
      hoverColorClassName = 'hover:text-foreground',
      children,
    } = props
    const { expanded: sidebarExpanded, side, setExpanded } = useSideNavContext()
    const routerState = useRouterState()
    const currentPath = routerState.location.pathname

    const active = useMemo(() => {
      if (typeof isActiveProp === 'boolean') {
        return isActiveProp
      }
      if (typeof isActiveProp === 'function') {
        return isActiveProp({ pathname: currentPath })
      }
      return false
    }, [isActiveProp, currentPath])

    const [open, setOpen] = useState(defaultExpandedProp || active)

    useEffect(() => {
      if (active) {
        setOpen(true)
      }
    }, [active])

    const ChevronIcon = open ? ChevronDownIcon : ChevronRightIcon

    const popoverSide = side === 'left' ? 'right' : 'left'

    const triggerClassName = cn(
      'relative mx-2 flex min-h-10 w-full cursor-pointer items-center gap-3 overflow-hidden rounded-md border-none bg-transparent px-3 py-[9px] text-left transition-colors',
      active ? activeBgClassName : inactiveBgClassName,
      hoverBgClassName,
      active ? 'text-foreground' : colorClassName,
      !active && hoverColorClassName
    )

    const triggerInner = (
      <>
        <div className="relative flex shrink-0 items-center gap-2 [&_svg]:size-5">
          {sidebarExpanded ? (
            <span
              aria-hidden
              className={cn('h-5 w-0.5 shrink-0 self-center rounded-sm', active ? ACTIVE_BAR_CLASS : 'bg-transparent')}
            />
          ) : null}
          <span className="inline-flex">{icon}</span>
          <NavBadge badge={badge} />
        </div>

        <div className="flex min-w-0 flex-1 justify-start overflow-hidden text-left">
          <div
            className="min-w-0 overflow-hidden"
            style={{
              maxWidth: sidebarExpanded ? undefined : '0px',
              opacity: sidebarExpanded ? 1 : 0,
              transition: sidebarExpanded ? LABEL_EXPANDED_TRANSITION : LABEL_COLLAPSED_TRANSITION,
            }}
          >
            <span className={cn('block truncate text-sm', active ? 'font-semibold' : 'font-medium')}>{label}</span>
          </div>
        </div>

        {sidebarExpanded ? <ChevronIcon aria-hidden className="size-4 shrink-0" /> : null}
      </>
    )

    if (!sidebarExpanded) {
      return (
        <Popover>
          <PopoverTrigger className={triggerClassName} ref={ref} type="button">
            {triggerInner}
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-2" side={popoverSide} sideOffset={8}>
            <p className="mb-1 px-2 pt-1 font-semibold text-muted-foreground text-xs">{label}</p>
            <SideNavContext.Provider value={{ expanded: true, side, setExpanded }}>{children}</SideNavContext.Provider>
          </PopoverContent>
        </Popover>
      )
    }

    return (
      <div>
        <button className={triggerClassName} onClick={() => setOpen((prev) => !prev)} ref={ref} type="button">
          {triggerInner}
        </button>
        <div
          className="overflow-hidden pl-3"
          style={{
            maxHeight: open ? '2000px' : '0px',
            opacity: open ? 1 : 0,
            transition: open
              ? 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease 0.05s'
              : 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.1s ease',
          }}
        >
          {children}
        </div>
      </div>
    )
  }
)

SideNavExpandableSection.displayName = 'SideNavExpandableSection'
